import moment from 'moment';
import history from '../../core/history';
import fetch from '../../core/fetch';
import showToaster from '../../helpers/showToaster';
import { getCalculationDetails } from '../../helpers/Pricing/getCalculationDetails';
import { getFixedValue } from '../../helpers/currencyConvertion';
import { getSpecialPricing as specialPricingQuery } from '../../lib/graphql';
import { userListingBook, createReservation as mutation } from '../../lib/graphql/reservation/book';
import {
  BOOKING_REQUEST_START,
  BOOKING_REQUEST_SUCCESS,
  BOOKING_REQUEST_ERROR,
} from '../../constants';

export function directBookingRequest(listId, guests, startDate, endDate, message) {
  return async (dispatch, getState, { client }) => {
    dispatch({ type: BOOKING_REQUEST_START, payload: { paymentLoading: true } });

    try {
      // 1. Fetch listing data (same query as bookingProcess)
      const { data: listingResult } = await client.query({
        query: userListingBook,
        variables: { listId: Number(listId) },
      });

      const bookingData = listingResult?.UserListing;
      if (!bookingData) {
        showToaster({ messageId: 'commonError', toasterType: 'error' });
        dispatch({ type: BOOKING_REQUEST_ERROR, payload: { paymentLoading: false } });
        return false;
      }

      const ld = bookingData.listingData;
      const checkInStr = moment(startDate).format('YYYY-MM-DD');
      const checkOutStr = moment(endDate).format('YYYY-MM-DD');

      // 2. Fetch special pricing for the chosen date range
      let specialPricing = [];
      try {
        const { data: spData } = await client.query({
          query: specialPricingQuery,
          variables: { listId, startDate: checkInStr, endDate: checkOutStr },
          fetchPolicy: 'network-only',
        });
        if (spData?.getSpecialPricing?.length > 0) {
          const seen = new Set();
          spData.getSpecialPricing.forEach((item) => {
            if (item.calendarStatus === 'available') {
              const key = moment(item.blockedDates).format('DD/MM/YYYY');
              if (!seen.has(key)) {
                seen.add(key);
                specialPricing.push({
                  listId: item.listId,
                  blockedDates: key,
                  isSpecialPrice: item.isSpecialPrice,
                  dayStatus: item.dayStatus,
                });
              }
            }
          });
        }
      } catch (_) { /* use empty specialPricing */ }

      // 3. Read currency + service fee state
      const state = getState();
      const serviceFees = state?.book?.serviceFees;
      const base = state?.currency?.base;
      const rates = state?.currency?.rates;
      const toCurrency = state?.currency?.to || state?.currency?.base;
      const guestId = state?.account?.data?.userId;
      const hostId = bookingData.userId;

      // 4. Compute all pricing (weekend prices, special prices, discounts, service fees)
      const {
        taxes,
        isSpecialPriceAssigned,
        guestServiceFee,
        hostServiceFee,
        bookingSpecialPricing,
        isAverage,
        total,
        discount,
        dayDifference,
        discountType,
        guestServiceFeeType,
        hostServiceFeeType,
        guestServiceFeeValue,
        hostServiceFeeValue,
        extraGuestFeeAmount,
        extraGuestCount,
      } = getCalculationDetails({
        startDate,
        endDate,
        serviceFees,
        specialPricing,
        currency: ld.currency,
        toCurrency,
        basePrice: ld.basePrice,
        cleaningPrice: ld.cleaningPrice || 0,
        monthlyDiscount: ld.monthlyDiscount || 0,
        weeklyDiscount: ld.weeklyDiscount || 0,
        customDiscounts: ld.customDiscounts || null,
        formatMessage: null,
        base,
        rates,
        tax: ld.tax || 0,
        extraGuestThreshold: ld.extraGuestThreshold || 0,
        extraGuestFee: ld.extraGuestFee || 0,
        weekendPrice: ld.weekendPrice || 0,
        guests,
      });

      // 5. Check the dates are still available
      const availResp = await fetch('/graphql', {
        method: 'post',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query checkReservation($checkIn:String,$checkOut:String,$listId:Int){
            checkReservation(checkIn:$checkIn,checkOut:$checkOut,listId:$listId){
              status errorMessage
            }
          }`,
          variables: { checkIn: checkInStr, checkOut: checkOutStr, listId: Number(listId) },
        }),
        credentials: 'include',
      });
      const { data: availData } = await availResp.json();

      if (availData?.checkReservation?.status !== '200') {
        showToaster({ messageId: 'checkAvailableDates', toasterType: 'error' });
        dispatch({ type: BOOKING_REQUEST_ERROR, payload: { paymentLoading: false } });
        return false;
      }

      // 6. Create reservation
      const cancellationPolicy = ld?.cancellation?.id;
      const { data } = await client.mutate({
        mutation,
        variables: {
          listId: Number(listId),
          hostId,
          guestId,
          checkIn: checkInStr,
          checkOut: checkOutStr,
          guests,
          message: message || '',
          basePrice: getFixedValue(ld.basePrice),
          cleaningPrice: getFixedValue(ld.cleaningPrice || 0),
          taxPrice: getFixedValue(taxes),
          currency: ld.currency,
          discount: getFixedValue(discount),
          discountType,
          guestServiceFee: getFixedValue(guestServiceFee),
          hostServiceFee: getFixedValue(hostServiceFee),
          total: getFixedValue(total),
          bookingType: 'request',
          paymentType: null,
          cancellationPolicy,
          specialPricing: JSON.stringify(bookingSpecialPricing),
          isSpecialPriceAssigned,
          isSpecialPriceAverage: parseFloat(getFixedValue(isAverage)),
          dayDifference,
          taxRate: ld.taxRate || 0,
          checkInStart: ld.checkInStart,
          checkInEnd: ld.checkInEnd,
          hostServiceFeeType,
          hostServiceFeeValue,
          threadId: null,
          guestServiceFeeType,
          guestServiceFeeValue,
          extraGuestFeeAmount: getFixedValue(extraGuestFeeAmount),
          extraGuestCount,
          extraGuestFee: getFixedValue(ld.extraGuestFee || 0),
          cautionDeposit: getFixedValue(ld.cautionDeposit || 0),
        },
      });

      if (data?.createReservation) {
        dispatch({
          type: BOOKING_REQUEST_SUCCESS,
          payload: { paymentLoading: false, reservationId: data.createReservation.id },
        });
        showToaster({ messageId: 'bookingRequestSent', toasterType: 'success' });
        history.push('/inbox');
        return { status: 200 };
      }

    } catch (error) {
      dispatch({ type: BOOKING_REQUEST_ERROR, payload: { error, paymentLoading: false } });
      showToaster({ messageId: 'commonError', toasterType: 'error' });
      return false;
    }

    return true;
  };
}
